<template>
  <!-- Fixed AI Summary Panel -->
  <div v-if="isVisible" class="ai-summary-panel">
    <button @click="$emit('close')" class="ai-summary-panel__close-btn">&times;</button>

    <div class="ai-summary-panel__content">
      <div v-if="contentSummary?.id" class="ai-summary-panel__toolbar">
        <SrpButton size="tiny" fill="outlined" color="gray" :disabled="isReprocessing" @click="reprocess">
          {{ isReprocessing ? "Reprocessing..." : "Reprocess" }}
        </SrpButton>
        <span v-if="displayStatus" class="ai-summary-panel__status">Status: {{ displayStatus }}</span>
        <span v-if="reprocessMessage" class="ai-summary-panel__message">{{ reprocessMessage }}</span>
      </div>

      <div class="ai-summary-raw">
        <pre>{{ displayContent }}</pre>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { ContentSummaryData } from "@contracts/contentCalendar";
import ContentSummaryRepo from "@repos/ContentSummaryRepo";
import SrpButton from "@components/ui/SrpButton.vue";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    customerId?: string;
    contentSummary?: ContentSummaryData | null;
  }>(),
  {
    isVisible: false,
    customerId: "",
    contentSummary: null,
  }
);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "reprocessed", contentSummary: ContentSummaryData): void;
}>();

const isReprocessing = ref(false);
const reprocessMessage = ref<string | null>(null);
const localStatus = ref<string | null>(null);

watch(
  () => props.contentSummary,
  () => {
    reprocessMessage.value = null;
    localStatus.value = props.contentSummary?.status ?? null;
  }
);

const displayStatus = computed(() => localStatus.value ?? props.contentSummary?.status ?? null);

const displayContent = computed(() => {
  const s = (props.contentSummary?.excludeFromIndex?.aiSummary ?? "").trim();
  if (!s) return "";

  if (s.startsWith("{") || s.startsWith("[")) {
    try {
      return JSON.stringify(JSON.parse(s), null, 2);
    } catch {
      return s;
    }
  }

  return s;
});

const reprocess = async () => {
  if (!props.customerId || !props.contentSummary?.id || isReprocessing.value) return;

  isReprocessing.value = true;
  reprocessMessage.value = null;
  const updated = await ContentSummaryRepo.reprocess(props.customerId, props.contentSummary.id);
  localStatus.value = updated.status;
  reprocessMessage.value = "Queued for reprocessing";
  emit("reprocessed", updated);
  isReprocessing.value = false;
};
</script>

<style scoped lang="scss">
// AI Summary Panel ===========================================================
.ai-summary-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  max-height: 60vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: rgba(0, 0, 0, 0.6);
    z-index: 1001;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
      color: rgba(0, 0, 0, 0.8);
    }
  }

  &__content {
    padding: 20px;
    padding-right: 60px; // Make room for close button
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  &__status {
    color: rgba(0, 0, 0, 0.6);
    font: 14px/20px sans-serif;
  }

  &__message {
    color: rgba(34, 134, 58, 1);
    font: 14px/20px sans-serif;
  }
}

// AI Summary Raw =============================================================
.ai-summary-raw {
  pre {
    background: rgba(248, 249, 250, 1);
    border: 1px rgba(224, 227, 231, 1) solid;
    border-radius: 6px;
    padding: 16px;
    font:
      14px/20px "SF Mono",
      Monaco,
      Consolas,
      "Liberation Mono",
      "Courier New",
      monospace;
    color: rgba(36, 41, 47, 1);
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-x: auto;
    margin: 0;
  }
}
</style>
